<template>
  <auth-shell>
    <p class="auth-form__eyebrow">
      Cotiza gratis
    </p>
    <h1 class="auth-form__title auth-form__title--with-lead">
      {{ $t('createAccount') }}
    </h1>
    <p class="auth-form__lead">
      Regístrate en un minuto y empieza a cotizar tu material.
    </p>

    <template v-if="googleEnabled">
      <google-sign-in-button
        text="signup_with"
        @success="enterApp"
      />
      <div class="auth-divider">
        <span>o con tu correo</span>
      </div>
    </template>

    <validation-observer ref="registerValidation">
      <b-form
        class="auth-form__fields"
        @submit.prevent="register()"
      >
        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Nombre"
          rules="required"
        >
          <label
            class="sr-only"
            for="register-name"
          >Nombre</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="UserIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="register-name"
              v-model="user.name"
              autocomplete="name"
              placeholder="Nombre"
            />
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Correo electrónico"
          rules="required|email"
        >
          <label
            class="sr-only"
            for="register-email"
          >Correo electrónico</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="MailIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="register-email"
              v-model="user.email"
              type="email"
              autocomplete="email"
              placeholder="Correo electrónico"
            />
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          tag="div"
          name="WhatsApp"
          rules="required|digits:10"
        >
          <label
            class="sr-only"
            for="register-phone"
          >WhatsApp</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="PhoneIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="register-phone"
              v-model="user.phone"
              autocomplete="tel-national"
              type="tel"
              inputmode="numeric"
              maxlength="10"
              placeholder="WhatsApp a 10 dígitos"
              @input="sanitizePhone"
            />
          </b-input-group>
          <small class="text-danger">{{ errors[0] }}</small>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          tag="div"
          name="Contraseña"
          rules="required|min:8"
        >
          <label
            class="sr-only"
            for="register-password"
          >Contraseña</label>
          <b-input-group :class="['auth-input', { 'auth-input--invalid': errors.length }]">
            <b-input-group-prepend is-text>
              <feather-icon icon="LockIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="register-password"
              v-model="password"
              :type="showPassword ? 'text' : 'password'"
              autocomplete="new-password"
              placeholder="Contraseña"
            />
            <b-input-group-append is-text>
              <button
                type="button"
                class="auth-input__toggle"
                :aria-label="showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'"
                @click="showPassword = !showPassword"
              >
                <feather-icon :icon="showPassword ? 'EyeOffIcon' : 'EyeIcon'" />
              </button>
            </b-input-group-append>
          </b-input-group>
          <small
            v-if="errors.length"
            class="text-danger"
          >{{ errors[0] }}</small>
          <small
            v-else
            class="auth-form__hint"
          >Mínimo 8 caracteres.</small>
        </validation-provider>

        <div v-if="showReferral">
          <label
            class="sr-only"
            for="register-referral"
          >Código de referido</label>
          <b-input-group class="auth-input">
            <b-input-group-prepend is-text>
              <feather-icon icon="GiftIcon" />
            </b-input-group-prepend>
            <b-form-input
              id="register-referral"
              ref="referralInput"
              v-model="user.referred_by"
              autocomplete="off"
              placeholder="Código de referido"
            />
          </b-input-group>
          <small class="auth-form__hint">Si alguien te invitó, su código te da beneficios.</small>
        </div>
        <b-link
          v-else
          class="auth-form__toggle-link"
          @click="openReferral"
        >
          ¿Tienes un código de referido?
        </b-link>

        <legal-consent
          v-if="legalRequired"
          v-model="acceptLegal"
          input-id="register-legal"
          :error="legalError"
        />

        <b-button
          type="submit"
          class="auth-submit"
          block
          :disabled="loading || !legalReady"
        >
          <b-spinner
            v-if="loading"
            small
          />
          <template v-else>
            <span>{{ $t('createAccount') }}</span>
            <feather-icon
              icon="ArrowRightIcon"
              size="18"
            />
          </template>
        </b-button>
      </b-form>
    </validation-observer>

    <p class="auth-form__footer">
      ¿Ya tienes cuenta?
      <b-link :to="{ name: 'auth-login' }">
        Inicia sesión
      </b-link>
    </p>

    <b-modal
      id="register-complete"
      ok-only
      centered
      ok-title="Entendido"
      title="Revisa tu correo"
    >
      <p class="mb-0">
        Te enviamos un correo para que crees tu contraseña. Ábrelo para entrar a YoConstruyo.
      </p>
    </b-modal>
  </auth-shell>
</template>

<script>
import { mapActions } from 'vuex'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BLink,
  BFormInput,
  BInputGroup,
  BInputGroupPrepend,
  BInputGroupAppend,
  BForm,
  BButton,
  BSpinner,
  BModal,
} from 'bootstrap-vue'
import {
  required, email, digits, min,
} from '@validations'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { startSession, landingRoute, googleSignInEnabled } from '@/auth/session'
import AuthShell from '@/components/auth/AuthShell.vue'
import GoogleSignInButton from '@/components/auth/GoogleSignInButton.vue'
import LegalConsent from '@/components/auth/LegalConsent.vue'

export default {
  components: {
    AuthShell,
    GoogleSignInButton,
    LegalConsent,
    BLink,
    BFormInput,
    BInputGroup,
    BInputGroupPrepend,
    BInputGroupAppend,
    BForm,
    BButton,
    BSpinner,
    BModal,
    ValidationProvider,
    ValidationObserver,
  },
  data() {
    return {
      loading: false,
      password: '',
      showPassword: false,
      showReferral: false,
      acceptLegal: false,
      legalError: '',
      legalReady: false,
      googleEnabled: googleSignInEnabled(),
      required,
      email,
      digits,
      min,
      user: {
        name: null,
        email: null,
        phone: '',
        supplier: null,
        role_name: 'customer',
        scoped_roles: [],
        role_resource_id: null,
        referred_by: '',
      },
    }
  },
  computed: {
    legalRequired() {
      return this.$store.getters['legal/currentDocuments'].length > 0
    },
  },
  watch: {
    acceptLegal(value) {
      if (value) this.legalError = ''
    },
  },
  mounted() {
    if (this.$route.query.referred_by) {
      this.user.referred_by = this.$route.query.referred_by
      this.showReferral = true
    }
    this.$store.dispatch('legal/fetchCurrentLegal')
      .catch(() => {})
      .finally(() => { this.legalReady = true })
  },
  methods: {
    ...mapActions('auth', ['createCustomer']),
    sanitizePhone(value) {
      this.user.phone = String(value || '').replace(/\D/g, '').slice(0, 10)
    },
    openReferral() {
      this.showReferral = true
      this.$nextTick(() => this.$refs.referralInput?.focus())
    },
    enterApp(payload) {
      const userData = startSession(payload)
      this.$router.replace(landingRoute(userData))
    },
    register() {
      this.$refs.registerValidation.validate().then(success => {
        this.legalError = this.legalRequired && !this.acceptLegal
          ? 'Para crear tu cuenta debes aceptar los Términos y el Aviso de Privacidad'
          : ''
        if (!success || this.legalError || this.loading || !this.legalReady) return

        this.loading = true
        this.createCustomer({
          ...this.user,
          phone: String(this.user.phone || '').replace(/\D/g, ''),
          password: this.password,
          acceptLegal: this.legalRequired ? this.acceptLegal : undefined,
          skipErrorToast: true,
        })
          .then(response => {
            if (response?.token) {
              this.enterApp(response)
              return
            }
            this.$bvModal.show('register-complete')
            this.resetForm()
          })
          .catch(error => {
            const messages = error?.messages
            this.$toast({
              component: ToastificationContent,
              position: 'top-center',
              props: {
                title: 'No pudimos crear tu cuenta',
                icon: 'AlertTriangleIcon',
                variant: 'danger',
                text: Array.isArray(messages) && messages.length ? messages.join(' ') : 'Inténtalo de nuevo en un momento.',
              },
            })
          })
          .finally(() => {
            this.loading = false
          })
      })
    },
    resetForm() {
      this.user.name = ''
      this.user.email = ''
      this.user.phone = ''
      this.user.referred_by = ''
      this.password = ''
      this.acceptLegal = false
      this.$nextTick(() => this.$refs.registerValidation.reset())
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../../components/auth/auth-page.scss';
</style>
